import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from '@mui/material';

import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { useUser } from '@hooks/useUser';
import {
  updateOperationIncident,
  OperationIncident
} from '@networkRequest/operationsService';
import { PrimaryButton, SecondaryButton } from '@components';
import Utils from '../../../../utils/utils';
import { validateActivityIncidentField } from '../../utils/formValidation';

dayjs.extend(customParseFormat);

interface FormErrors {
  date: string | null;
  time: string | null;
}

interface FormData {
  date: Dayjs;
  time: Dayjs;
}

const defaultFormErrors = {
  date: null,
  time: null,
};

const EditIncidentDateTimeDialog = ({
  open,
  onClose,
  incident,
  setIncident,
  setError,
}: {
  open: boolean;
  onClose: () => void;
  incident: OperationIncident;
  setIncident: Dispatch<SetStateAction<OperationIncident | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}) => {
  const { user } = useUser();

  if (!user) return;

  const formRef = useRef(null);

  const [formErrors, setFormErrors] = useState<FormErrors>(defaultFormErrors);
  const [formData, setFormData] = useState<FormData>({
    date: dayjs(),
    time: dayjs(),
  });

  useEffect(() => {
    if (!incident) return;
    setFormData({
      date: dayjs(incident?.performed_at),
      time: dayjs(incident?.performed_at),
    });
  }, [incident]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const { date, time } = parseFormData(formData);

    const { data, error } = await updateOperationIncident({
      accessToken: user.accessToken,
      incidentId: incident.id,
      payload: {
        performed_at: Utils.createTimestamp(date, time),
      }
    });

    if (data) {
      setIncident(data);
      onClose();
    } else {
      setError(error ?? 'Failed to update incident');
    }
  };

  const handleCancel = () => {
    setFormErrors(defaultFormErrors);
    onClose();
  };

  const handleChangeInput = (field: keyof typeof formData, value: any) => {
    validateActivityIncidentField({ field, value, stateHandler: setFormErrors, formErrors });
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
  };

  const parseFormData = (data: FormData) => {
    const { date, time } = data;
    return {
      date: date?.format('YYYY-MM-DD'),
      time: time?.format('HH:mm:ss'),
    };
  };

  return (
    <Dialog open={open}
      slotProps={{
        backdrop: { invisible: true },
        paper: { style: { borderRadius: 8, width: '783px' } }
      }}
    >
      <DialogTitle sx={{ padding: 3 }} variant='h5'>
        Edit Incident Date & Time
      </DialogTitle>
      <form ref={formRef} onSubmit={handleSubmit} noValidate>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  name='date'
                  label='Date'
                  value={formData.date}
                  onChange={(value) => handleChangeInput('date', value)}
                  disableFuture
                  slotProps={{
                    textField: {
                      required: true,
                      error: !!formErrors.date,
                      onChange: (value) => handleChangeInput('date', value),
                      helperText: formErrors.date ?? '',
                      sx: {
                        width: '100%',
                        '& fieldset': {
                          borderRadius: '10px',
                          borderColor: '#E0E0E0', // match the border color of the rest of the mui inputs
                        },
                      },
                      slotProps: { inputLabel: { shrink: true } },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  name='time'
                  label='Time'
                  value={formData.time}
                  onChange={(value) => handleChangeInput('time', value)}
                  slotProps={{
                    textField: {
                      required: true,
                      error: !!formErrors.time,
                      onChange: (value) => handleChangeInput('time', value),
                      helperText: formErrors.time ?? '',
                      sx: {
                        width: '100%',
                        '& fieldset': {
                          borderRadius: '10px',
                          borderColor: '#E0E0E0', // match the border color of the rest of the mui inputs
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            paddingTop: 1,
            paddingBottom: 3,
            paddingRight: 3,
            paddingLeft: 3,
          }}
        >
          <SecondaryButton action={handleCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type='submit' disabled={!!(formErrors.date || formErrors.time)}>
            Submit
          </PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditIncidentDateTimeDialog;
